import { z } from 'zod';

// Validators - Exact match to Flutter validation logic
// Source: centrajob_frontend_college/lib/utils/validator_helper.dart

/**
 * Email validation
 * Regex: word+([-+.']word)*@word([-.]\word)*.(com|edu|org|in|net|gov)
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^(?:\w+([-+.']\w+)*@\w+([-.]\w+)*\.(com|edu|org|in|net|gov))$/i;
  return emailRegex.test(email.toLowerCase());
};

/**
 * Email or Phone validation (for login)
 * Accepts email format OR 10-digit phone number
 */
export const validateEmailOrPhone = (value: string): boolean => {
  if (validateEmail(value)) return true;
  const phoneRegex = /^\d{10}$/;
  return phoneRegex.test(value);
};

/**
 * Phone number validation
 * - Must be exactly 10 digits
 *
 * The Flutter validator additionally blocklisted all-same-digit numbers and the
 * sequences 1234567890 / 9876543210. Those were dropped: they rejected input
 * that is structurally valid, so any 10-digit number is accepted here.
 */
export const validatePhoneNumber = (phone: string): boolean => {
  const phoneRegex = /^\d{10}$/;
  return phoneRegex.test(phone);
};

/**
 * Normalise raw phone input (typed or pasted) down to at most 10 digits.
 * Strips separators/whitespace first, then a pasted country code (+91 / 0091)
 * or leading trunk zero, so numbers copied from a sheet or contact card land
 * as a clean 10-digit value instead of being truncated mid-way.
 */
export const sanitizePhoneNumberInput = (value: string): string => {
  let digits = value.replace(/\D/g, '');
  if (digits.length > 10) digits = digits.replace(/^0+/, '');
  if (digits.length > 10 && digits.startsWith('91')) digits = digits.slice(2);
  return digits.slice(0, 10);
};

/**
 * Password validation - Match Flutter exactly
 * Requirements:
 * - Minimum 8 characters
 * - At least 1 uppercase letter [A-Z]
 * - At least 1 lowercase letter [a-z]
 * - At least 1 digit [0-9]
 * - At least 1 special character [@$!%*?&#]
 */
export const validatePassword = (password: string): boolean => {
  const passwordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;
  return passwordRegex.test(password);
};

/**
 * Password validation result with individual checks
 */
export interface PasswordValidationResult {
  isValid: boolean;
  hasMinLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
}

export const getPasswordValidationResult = (password: string): PasswordValidationResult => {
  return {
    isValid: validatePassword(password),
    hasMinLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecialChar: /[@$!%*?&#]/.test(password),
  };
};

/**
 * Name validation (for admin name)
 */
export const validateName = (name: string): boolean => {
  const nameRegex = /^[A-Za-z]+([A-Za-z '.]*[A-Za-z])?$/;
  return nameRegex.test(name);
};

/**
 * College name validation
 */
export const validateCollegeName = (name: string): boolean => {
  const collegeNameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ&.,''\-][A-Za-zÀ-ÖØ-öø-ÿ&.,''\- ]{1,99}$/;
  return collegeNameRegex.test(name);
};

/**
 * Postal code validation (India - 6 digits)
 */
export const validatePostalCode = (code: string): boolean => {
  const postalCodeRegex = /^[1-9][0-9]{5}$/;
  return postalCodeRegex.test(code);
};

/**
 * OTP validation (6 digits)
 */
export const validateOtp = (otp: string): boolean => {
  return /^\d{6}$/.test(otp);
};

/**
 * Website / URL validation
 * Accepts optional http(s)://, requires a domain with at least one dot and a 2+ char TLD.
 */
export const validateWebsiteUrl = (url: string): boolean => {
  const urlRegex = /^(https?:\/\/)?([\w-]+\.)+[A-Za-z]{2,}(\/[^\s]*)?$/i;
  return urlRegex.test(url.trim());
};

/**
 * Designation validation
 * Allows letters, spaces, and common punctuation (.,&-/'); rejects any digit.
 * Must contain at least one letter.
 */
export const validateDesignation = (value: string): boolean => {
  const designationRegex = /^(?=.*[A-Za-z])[A-Za-zÀ-ÖØ-öø-ÿ&.,''\-/ ]+$/;
  return designationRegex.test(value);
};

/**
 * Alphanumeric name validation
 * Allows letters, digits, spaces and common punctuation; requires at least one letter
 * (so values made entirely of digits are rejected).
 */
export const validateAlphanumericName = (value: string): boolean => {
  const regex = /^(?=.*[A-Za-z])[A-Za-z0-9À-ÖØ-öø-ÿ&.,''()\-/ ]+$/;
  return regex.test(value);
};

/**
 * Free-text validation
 * Requires at least one alphabetic character — accepts anything else (newlines, punctuation, digits).
 * Used for long-form fields like descriptions where we only want to reject pure-digit / pure-symbol input.
 */
export const hasAtLeastOneLetter = (value: string): boolean => /[A-Za-z]/.test(value);

// Zod Schemas - Updated to match Flutter validation

export const emailSchema = z.string()
  .min(1, 'Email is required')
  .refine(validateEmail, 'Please enter a valid email address');

export const emailOrPhoneSchema = z.string()
  .min(1, 'Email or phone number is required')
  .refine(validateEmailOrPhone, 'Please enter a valid email or 10-digit phone number');

export const passwordSchema = z.string()
  .min(8, 'Password must be at least 8 characters')
  .refine((val) => /[A-Z]/.test(val), 'Password must contain at least 1 uppercase letter')
  .refine((val) => /[a-z]/.test(val), 'Password must contain at least 1 lowercase letter')
  .refine((val) => /\d/.test(val), 'Password must contain at least 1 number')
  .refine((val) => /[@$!%*?&#]/.test(val), 'Password must contain at least 1 special character (@$!%*?&#)');

export const phoneSchema = z.string()
  .min(1, 'Phone number is required')
  .refine(validatePhoneNumber, 'Please enter a valid 10-digit phone number');

export const nameSchema = z.string()
  .min(1, 'Name is required')
  .refine(validateName, 'Please enter a valid name');

export const collegeNameSchema = z.string()
  .min(1, 'College/Institute name is required')
  .refine(validateCollegeName, 'Please enter a valid college/institute name');

export const pincodeSchema = z.string()
  .min(1, 'Postal code is required')
  .refine(validatePostalCode, 'Please enter a valid 6-digit postal code');

export const otpSchema = z.string()
  .length(6, 'OTP must be 6 digits')
  .refine(validateOtp, 'Please enter a valid 6-digit OTP');

// Form validation schemas
export const loginFormSchema = z.object({
  userName: emailSchema,
  password: z.string().min(1, 'Password is required'),
});

export const registerFormSchema = z.object({
  universityUId: z.string().min(1, 'Please select a university'),
  collegeUId: z.string().optional(),
  collegeName: collegeNameSchema,
  collegeEmail: emailSchema,
  adminName: nameSchema,
  adminEmail: emailSchema,
  phoneNumber: phoneSchema,
  agreeToTerms: z.boolean().refine((val) => val === true, 'You must agree to the terms and conditions'),
});

export const setPasswordFormSchema = z.object({
  password: passwordSchema,
  confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

export const resetPasswordEmailSchema = z.object({
  email: emailSchema,
});

export const resetPasswordNewSchema = z.object({
  password: passwordSchema,
  confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

export const verificationCodeSchema = z.object({
  otp: otpSchema,
});

// Validation helper functions
export const isValidEmail = (email: string): boolean => {
  return emailSchema.safeParse(email).success;
};

export const isValidPhone = (phone: string): boolean => {
  return phoneSchema.safeParse(phone).success;
};

export const isValidPassword = (password: string): boolean => {
  return passwordSchema.safeParse(password).success;
};

export const isValidPincode = (pincode: string): boolean => {
  return pincodeSchema.safeParse(pincode).success;
};

// File validation
export const isValidFileType = (file: File, allowedTypes: string[]): boolean => {
  return allowedTypes.includes(file.type);
};

export const isValidFileSize = (file: File, maxSizeInMB: number): boolean => {
  return file.size <= maxSizeInMB * 1024 * 1024;
};

export const validateFile = (
  file: File,
  options: {
    maxSizeInMB?: number;
    allowedTypes?: string[];
  } = {}
): { valid: boolean; error?: string } => {
  const { maxSizeInMB = 5, allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'] } = options;

  if (!isValidFileSize(file, maxSizeInMB)) {
    return { valid: false, error: `File size must be less than ${maxSizeInMB}MB` };
  }

  if (!isValidFileType(file, allowedTypes)) {
    return { valid: false, error: 'Invalid file type' };
  }

  return { valid: true };
};

// Error messages object for convenience
export const ValidationMessages = {
  email: {
    required: 'Email is required',
    invalid: 'Please enter a valid email address',
  },
  emailOrPhone: {
    required: 'Email or phone number is required',
    invalid: 'Please enter a valid email or 10-digit phone number',
  },
  phone: {
    required: 'Phone number is required',
    invalid: 'Please enter a valid 10-digit phone number',
  },
  password: {
    required: 'Password is required',
    invalid: 'Password must be at least 8 characters with 1 uppercase, 1 lowercase, 1 number, and 1 special character (@$!%*?&#)',
    minLength: 'Password must be at least 8 characters',
    uppercase: 'Password must contain at least 1 uppercase letter',
    lowercase: 'Password must contain at least 1 lowercase letter',
    number: 'Password must contain at least 1 number',
    specialChar: 'Password must contain at least 1 special character (@$!%*?&#)',
  },
  confirmPassword: {
    required: 'Please confirm your password',
    mismatch: 'Passwords do not match',
  },
  name: {
    required: 'Name is required',
    invalid: 'Please enter a valid name',
  },
  collegeName: {
    required: 'College/Institute name is required',
    invalid: 'Please enter a valid college/institute name',
  },
  postalCode: {
    required: 'Postal code is required',
    invalid: 'Please enter a valid 6-digit postal code',
  },
  otp: {
    required: 'OTP is required',
    invalid: 'Please enter a valid 6-digit OTP',
  },
  website: {
    invalid: 'Please enter a valid website URL',
  },
  designation: {
    required: 'Designation is required',
    invalid: 'Please enter a valid designation',
  },
};
